<template>
  <aw-section-wrapper ref="componentRootElement" :has-padding="false">
    <template #content="{ contentEvents: { contentLoading } }">
      <aw-tab-carousel
        :main-title="getCategoryDisplayName(category)"
        :empty-placeholder="$awt('aw.shortlist.fallback.text')"
        :tabs="tabs"
        :breakpoints="breakpoints"
        :slides="slides"
        tab-key="id"
        slide-key="id"
        slide-group-holder-tag="ul"
        :class="[$style.tabCarousel]"
        @tab-opened="selectCategoryTab(contentLoading, $event.newTabItem)"
      >
        <template #slide(all)(all)="{ slide, slideIdx }">
          <li :aria-labelledby="`${componentId}_${slide.id}`">
            <h3
              v-if="slide.selectedVariant?.name"
              :id="`${componentId}_${slide.id}`"
              class="awSrOnly"
              v-text="slide.selectedVariant.name"
            />
            <div
              :class="[
                'awProductCardPlaceholder',
                $style.tabCarouselPlaceHolder,
              ]"
            >
              <aw-if-visible
                :initial-visibility="slideIdx === 0"
                :root-margin="(w) => `${w.innerHeight}px 300px`"
                :disabled="slideIdx === 0"
                :class="$style.ifVisible"
              >
                <div :class="$style.tabCarouselItemWrapper">
                  <aw-product-card-version2
                    v-if="!slide.isPlaceholder && productCardSize"
                    :key="slide.id"
                    :product-data="slide"
                    :has-compare="false"
                    :class="[$style.tabCarouselItem, $style.tabCarouselItemOffer]"
                    :card-size="productCardSize"
                    :opened-details="false"
                    heading-tag="h4"
                  />
                </div>
              </aw-if-visible>
            </div>
            <aw-hook v-if="slideIdx === 0" @mounted="onPlaceholderMounted" />
          </li>
        </template>
      </aw-tab-carousel>
      <div :class="$style.allProductsButtonWrapper">
        <lv-button styling="secondary-dark" shrink v-bind="{to: getCategoryUrl(category)}">
          {{ $awt('aw.shortlist.show_all_products') }}
        </lv-button>
      </div>
    </template>
  </aw-section-wrapper>
</template>

<script setup>
  import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, useId, useTemplateRef, watch } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { UnCachedProduct, updateProdWithUnCachedData, useProductsStore } from '~~/common/stores/products';
  import { getCategoryUrl } from '~~/common/composables/useUrls';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index';
  import AwIfVisible from '~~/common/components/Common/AwIfVisible.vue';
  import AwHook from '~~/common/components/Common/AwHook.vue';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { getProductCardSizeV2 } from '~~/common/utils/productCard';
  import { getCategoryDisplayName } from '~~/common/utils/category';

  const AwTabCarousel = defineAsyncComponent(() => import('~~/common/components/AwTabCarousel'));
  const AwProductCardVersion2 = defineAsyncComponent(() => import('~~/common/components/Common/AwProductCardVersion2'));
  const AwSectionWrapper = defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper'));

  defineOptions({
    name: 'AwCategoryPreviewCarousel',
    inheritAttrs: false,
  });

  const props = defineProps({
    category: {
      type: Object,
      required: true,
    },
  });

  const { $awEvents, $logger } = useNuxtApp();
  const userInterfaceStore = useUserInterfaceStore();

  const componentId = useId();
  const componentRootElement = useTemplateRef('componentRootElement');
  const productCardSize = ref(null);
  const selectedTabId = ref(null);
  let rAFId = null;

  const screenRange = computed(() => userInterfaceStore.mediaQueries);
  const tabs = computed(() => getTabs());
  const breakpoints = computed(() => {
    return [
      {
        at: screenRange.value['desktop-large-min'],
        count: 5,
      },
      {
        at: screenRange.value['desktop-small-min'],
        count: 4,
      },
      {
        at: screenRange.value['tablet-min'],
        count: 3,
      },
      {
        at: true,
        count: 'auto',
      },
    ];
  });

  const slides = ref(Object.fromEntries(getTabs().map(e => [e?.id, []])));

  watch(() => tabs.value.length, (newVal) => {
    if (Object.keys(slides.value).length !== newVal) {
      initSlides();
    }
  });

  onMounted(() => {
    $awEvents.addEvent('throttled-resize', setCardSize);
    initSlides();
  });

  onUnmounted(() => {
    $awEvents.removeEvent('throttled-resize', setCardSize);
  });

  function onPlaceholderMounted () {
    setCardSize();
  }

  function setCardSize () {
    cancelAnimationFrame(rAFId);
    rAFId = requestAnimationFrame(() => {
      const placeholder = (componentRootElement.value?.$el
        ?.querySelector?.('.awProductCardPlaceholder')
      );
      const clientWidth = placeholder?.clientWidth;
      const pcs = getProductCardSizeV2(clientWidth);
      if (pcs) {
        productCardSize.value = pcs;
      }
    });
  }

  function initSlides () {
    const placeholders = Array(breakpoints.value.find(b => b.at).count).fill();
    tabs.value.forEach((tab) => {
      if (!slides.value[tab.id]?.length) {
        slides.value[tab.id] = placeholders.map((_, idx) => ({
          id: idx,
          isPlaceholder: true,
        }));
      }
    });
  }

  async function selectCategoryTab (updateLoadingState, { id }) {
    const productsStore = useProductsStore();
    selectedTabId.value = id;
    if (slides.value[id]?.length && !slides.value[id][0]?.isPlaceholder) {
      return;
    }
    if (import.meta.server) {
      // NOTE: AUCHSUP-6691 Aborts here because SSR won't wait for async event
      // handler to finish.
      return;
    }
    updateLoadingState(true);
    const params = {
      isCached: true,
      categoryId: id,
      page: 1,
      itemsPerPage: 12,
      filterParams: {
        available: 1,
      },
    };

    const {
      value: data,
      isCached,
    } = await productsStore.fetchCachedProductOrFallback({ params });
    slides.value[id] = data?.results;
    if (isCached) {
      const resp = await productsStore.getUnCachedPartsReq({ productIds: (slides.value[id] || []).map(p => p.id) });
      if (resp?.list && Object.keys(resp.list).length) {
        for (const k in resp.list) {
          resp.list[k] = new UnCachedProduct(resp.list[k]);
        }
        updateProdWithUnCachedData({
          productsToMutate: slides.value[id],
          payload: resp.list,
          $logger,
        });
      }
    }

    updateLoadingState(false);
  }

  function getTabs () {
    return [{
      id: props.category.id,
      tabTitle: null,
    }];
  }
</script>

<style module lang="scss" rel="stylesheet/scss">

.tabCarousel {
  --awTabItemPadding: 14px 16px;

  @include tablet(min) {
    --awTabItemPadding: 12px 16px;
  }

  &PlaceHolder {
    display: flex;
    flex: 1;
    @include tabCarouselCardPlaceholder();
  }

  .ifVisible,
  .tabCarouselItemWrapper {
    display: flex;
    flex: 1;
  }

  &Item {
    flex: 1;
  }
}

.allProductsButtonWrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  @include tablet(min) {
    margin-top: 40px;
  }
}
</style>
